import React from 'react';
import { Stack } from '@fluentui/react';
import { useStyles } from './index.styles';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';
import { EntityTagInput } from 'common/types/globalTypes';
import {
  TravelerAvailableTags,
  TravelerAvailableTagsVariables,
} from 'common/graphql/__generated__/TravelerAvailableTags';
import {
  TravelerTags,
  TravelerTagsVariables,
} from 'common/graphql/__generated__/TravelerTags';
import {
  EntityTagTravelerCreate,
  EntityTagTravelerCreateVariables,
} from 'common/graphql/__generated__/EntityTagTravelerCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';

import ENTITY_TAG_TRAVELER_CREATE from 'common/graphql/EntityTagTravelerCreate.graphql';
import TRAVELER_AVAILABLE_TAGS from 'common/graphql/TravelerAvailableTags.graphql';
import TRAVELER_TAGS from 'common/graphql/TravelerTags.graphql';
import ENTITY_TAG_DELETE from 'common/graphql/EntityTagDelete.graphql';

interface TagsProps {
  travelerId: string;
}

export const Tags: React.FC<TagsProps> = ({ travelerId }) => {
  const styles = useStyles();
  // Memoized functions for extracting data and generating variables
  const extractAvailableTags = React.useCallback(
    (data: TravelerAvailableTags | undefined) =>
      data?.travelerAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? [],
    []
  );

  const extractEntityTags = React.useCallback(
    (data: TravelerTags | undefined) =>
      data?.traveler?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown',
      })) ?? [],
    []
  );

  const getAvailableTagsVars = React.useCallback(
    (entityId: string): TravelerAvailableTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const getEntityTagsVars = React.useCallback(
    (entityId: string): TravelerTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const createTagsVars = React.useCallback(
    (tags: EntityTagInput[]): EntityTagTravelerCreateVariables => ({
      input: {
        entityTag: tags,
      },
    }),
    []
  );

  const deleteTagsVars = React.useCallback(
    (id: string, rowTimestamp: string): EntityTagDeleteVariables => ({
      entityTagDeleteInput: {
        entityDelete: [{ id, rowTimestamp }],
      },
    }),
    []
  );

  return (
    <Stack
      tokens={{ childrenGap: 15, padding: '0px 20px' }}
      className={styles.container}
    >
      <EntityTagsSection<
        TravelerAvailableTags,
        TravelerAvailableTagsVariables,
        TravelerTags,
        TravelerTagsVariables,
        EntityTagTravelerCreate,
        EntityTagTravelerCreateVariables,
        EntityTagDelete,
        EntityTagDeleteVariables
      >
        entityId={travelerId}
        getAvailableTagsGql={TRAVELER_AVAILABLE_TAGS}
        getEntityTagsGql={TRAVELER_TAGS}
        addTagGql={ENTITY_TAG_TRAVELER_CREATE}
        deleteTagGql={ENTITY_TAG_DELETE}
        extractAvailableTags={extractAvailableTags}
        extractEntityTags={extractEntityTags}
        getAvailableTagsVars={getAvailableTagsVars}
        getEntityTagsVars={getEntityTagsVars}
        createTagsVars={createTagsVars}
        deleteTagsVars={deleteTagsVars}
      />
    </Stack>
  );
};
